import { createApp, type App, type ComponentPublicInstance, nextTick } from 'vue'
import type { ToastConfig } from '@/types/dialog'
import Toast from '@/components/base/toast/Toast.vue'

// 定义 Toast 组件实例类型
type ToastInstance = ComponentPublicInstance & {
  show: (config: ToastConfig) => void
  hide: () => void
}

// 相对定位配置
export interface RelativeToastConfig extends ToastConfig {
  container: HTMLElement | string | (() => HTMLElement) // 挂载容器
  position?: 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center' // 相对位置
  offset?: { x?: number; y?: number } // 偏移量
}

// 相对 Toast 实例管理
class RelativeToastService {
  private instances: Array<{ app: App; container: HTMLElement; instance: ToastInstance }> = []

  private safeDestroy(app: App, container: HTMLElement) {
    try {
      app.unmount()
    } catch (error) {
      console.error('RelativeToast unmount error:', error)
    }

    try {
      container.parentNode?.removeChild(container)
    } catch (error) {
      console.error('RelativeToast remove error:', error)
    }
  }

  /**
   * 获取元素（支持 HTMLElement、选择器字符串、函数）
   */
  private getElement(
    element: HTMLElement | string | (() => HTMLElement) | undefined
  ): HTMLElement | null {
    if (!element) return null
    if (typeof element === 'string') {
      return document.querySelector(element)
    }
    if (typeof element === 'function') {
      return element()
    }
    return element
  }

  /**
   * 确保容器有相对定位
   */
  private ensureRelativePosition(container: HTMLElement) {
    const style = window.getComputedStyle(container)
    if (style.position === 'static') {
      container.style.position = 'relative'
    }
  }

  /**
   * 计算相对位置（相对于容器）
   */
  private calculatePosition(
    toastElement: HTMLElement,
    container: HTMLElement,
    position: string = 'top',
    offset: { x?: number; y?: number } = {}
  ) {
    const containerRect = container.getBoundingClientRect()
    const toastRect = toastElement.getBoundingClientRect()
    const containerWidth = containerRect.width
    const containerHeight = containerRect.height

    const offsetX = offset.x || 0
    const offsetY = offset.y || 0

    let top = 0
    let left = 0

    switch (position) {
      case 'top':
        top = 10 + offsetY
        left = (containerWidth - toastRect.width) / 2 + offsetX
        break
      case 'bottom':
        top = containerHeight - toastRect.height - 10 + offsetY
        left = (containerWidth - toastRect.width) / 2 + offsetX
        break
      case 'top-left':
        top = 10 + offsetY
        left = 10 + offsetX
        break
      case 'top-right':
        top = 10 + offsetY
        left = containerWidth - toastRect.width - 10 + offsetX
        break
      case 'bottom-left':
        top = containerHeight - toastRect.height - 10 + offsetY
        left = 10 + offsetX
        break
      case 'bottom-right':
        top = containerHeight - toastRect.height - 10 + offsetY
        left = containerWidth - toastRect.width - 10 + offsetX
        break
      case 'center':
      default:
        top = (containerHeight - toastRect.height) / 2 + offsetY
        left = (containerWidth - toastRect.width) / 2 + offsetX
    }

    toastElement.style.position = 'absolute'
    toastElement.style.top = `${top}px`
    toastElement.style.left = `${left}px`
    toastElement.style.transform = 'none'
    toastElement.style.zIndex = '9999'
  }

  /**
   * 显示相对定位 Toast
   */
  show(config: RelativeToastConfig) {
    // 确定挂载容器
    const container = this.getElement(config.container)
    if (!container) {
      console.warn('RelativeToast: container element not found')
      return
    }

    // 确保容器有相对定位
    this.ensureRelativePosition(container)

    // 创建容器
    const toastContainer = document.createElement('div')
    container.appendChild(toastContainer)

    // 创建 Toast 实例
    const app = createApp(Toast)
    const instance = app.mount(toastContainer) as ToastInstance

    // 记录实例
    this.instances.push({ app, container: toastContainer, instance })

    // 显示 Toast
    instance.show({
      ...config,
      onClose: () => {
        config.onClose?.()
        this.destroyInstance(app, toastContainer)
      }
    })

    // 计算并设置位置
    nextTick(() => {
      // Toast 组件的根元素就是 .toast-container
      const toastElement = (toastContainer.firstElementChild ||
        toastContainer.querySelector('.toast-container')) as HTMLElement
      if (toastElement) {
        this.calculatePosition(toastElement, container, config.position || 'top', config.offset)
      }
    })

    return instance
  }

  /**
   * 成功提示
   */
  success(message: string, options?: Partial<RelativeToastConfig>) {
    return this.show({
      message,
      type: 'success',
      ...options
    } as RelativeToastConfig)
  }

  /**
   * 错误提示
   */
  error(message: string, options?: Partial<RelativeToastConfig>) {
    return this.show({
      message,
      type: 'error',
      ...options
    } as RelativeToastConfig)
  }

  /**
   * 警告提示
   */
  warning(message: string, options?: Partial<RelativeToastConfig>) {
    return this.show({
      message,
      type: 'warning',
      ...options
    } as RelativeToastConfig)
  }

  /**
   * 信息提示
   */
  info(message: string, options?: Partial<RelativeToastConfig>) {
    return this.show({
      message,
      type: 'info',
      ...options
    } as RelativeToastConfig)
  }

  /**
   * 加载提示
   */
  loading(message: string = 'Loading...', options?: Partial<RelativeToastConfig>) {
    return this.show({
      message,
      type: 'loading',
      duration: 0,
      ...options
    } as RelativeToastConfig)
  }

  /**
   * 销毁实例
   */
  private destroyInstance(app: App, container: HTMLElement) {
    this.safeDestroy(app, container)

    const index = this.instances.findIndex(
      (item) => item.app === app && item.container === container
    )
    if (index > -1) {
      this.instances.splice(index, 1)
    }
  }

  /**
   * 清除所有 Toast
   */
  clear() {
    const instancesToClear = [...this.instances]
    this.instances = []

    instancesToClear.forEach(({ app, container, instance }) => {
      try {
        instance.hide()
        setTimeout(() => {
          this.safeDestroy(app, container)
        }, 300)
      } catch (error) {
        console.warn('Failed to hide relativeToast:', error)
        this.safeDestroy(app, container)
      }
    })
  }
}

// 创建实例
const relativeToast = new RelativeToastService()

export default relativeToast
export { RelativeToastService }
